import React, { useState, useEffect, useCallback, useRef } from 'react';
import { connect, useSelector } from 'react-redux';
import {
    Paper, Button, Chip, Checkbox, FormControlLabel, TextField, Typography, Divider,
    Radio, RadioGroup, FormControl, FormLabel, Dialog, DialogTitle, DialogContent,
    DialogContentText, DialogActions, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TablePagination, Tooltip
} from '@material-ui/core';
import { Autocomplete, ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep';
import PeopleIcon from '@material-ui/icons/People';
import moment from 'moment';

import HttpService from '../../services/httpService';
import { setLoading, setSnackInfo } from '../../redux/actions/appAction';
import { ROLENAME } from '../../utils/index';
import { formatCountdown, remainingClearMs, CACHE_CLEAR_WINDOW_MS } from '../../utils/clearClientStorage';
import AccessDenied from '../../components/accessDeniedPage';


const useStyles = makeStyles(() => ({
    page: {
        padding: 24,
        maxWidth: 1400,
        margin: '0 auto',
    },
    title: {
        fontSize: 26,
        fontWeight: 700,
        color: '#2b2b2b',
        margin: 0,
    },
    subtitle: {
        fontSize: 14,
        color: '#777',
        marginTop: 4,
        marginBottom: 20,
    },
    card: {
        padding: 20,
        borderRadius: 8,
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        marginBottom: 20,
    },
    cardHeading: {
        fontSize: 16,
        fontWeight: 700,
        color: '#1C375D',
        marginBottom: 14,
    },
    toggleGroup: {
        backgroundColor: '#fff',
        marginBottom: 16,
        flexWrap: 'wrap',
    },
    toggleBtn: {
        textTransform: 'none',
        fontWeight: 600,
        color: '#666',
        border: '1px solid #ccc',
        padding: '6px 18px',
        '&.Mui-selected': {
            backgroundColor: '#1C375D',
            color: '#FFF2EB',
            borderColor: '#1C375D',
            '&:hover': { backgroundColor: '#1C375D' },
        },
    },
    modeOption: {
        display: 'block',
        marginBottom: 6,
    },
    modeHint: {
        fontSize: 13,
        color: '#777',
        marginLeft: 32,
        marginBottom: 10,
    },
    impactBar: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        backgroundColor: '#FFF2EB',
        borderRadius: 8,
        padding: '12px 16px',
        marginBottom: 16,
    },
    impactCount: {
        fontSize: 18,
        fontWeight: 700,
        color: '#1C375D',
    },
    sampleChip: {
        backgroundColor: '#fff',
    },
    actionBtn: {
        backgroundColor: '#355025',
        color: '#FFF2EB',
        textTransform: 'none',
        fontWeight: 600,
        padding: '8px 22px',
        '&:hover': { backgroundColor: '#28401c' },
        '&.Mui-disabled': { backgroundColor: '#cfcfcf', color: '#fff' },
    },
    dangerBtn: {
        backgroundColor: '#C0392B',
        color: '#fff',
        textTransform: 'none',
        fontWeight: 600,
        padding: '8px 22px',
        '&:hover': { backgroundColor: '#a03024' },
        '&.Mui-disabled': { backgroundColor: '#cfcfcf', color: '#fff' },
    },
    tableContainer: {
        borderRadius: 8,
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    },
    headCell: {
        padding: 12,
        textAlign: 'center',
        fontWeight: 700,
        backgroundColor: '#1C375D',
        color: '#FFF2EB',
    },
    bodyCell: {
        padding: 12,
        textAlign: 'center',
    },
    rowHover: {
        '&:hover': { backgroundColor: '#f5f7f2' },
    },
    modeChipSoft: {
        backgroundColor: '#e3f2fd',
        color: '#1565c0',
        fontWeight: 600,
    },
    modeChipHard: {
        backgroundColor: '#fdecea',
        color: '#c0392b',
        fontWeight: 600,
    },
    countdown: {
        fontSize: 12,
        fontWeight: 600,
        color: '#F47D44',
        marginTop: 4,
        whiteSpace: 'nowrap',
    },
}));

const TARGET_TYPES = [
    { value: 'users', label: 'Specific Users' },
    { value: 'roles', label: 'By Role' },
    { value: 'departments', label: 'By Department' },
    { value: 'all', label: 'All Users' },
];

const CONFIRM_PHRASE = 'CLEAR ALL USERS';
const PREVIEW_DEBOUNCE_MS = 400;

// After a row's countdown ends, re-read the history at these delays so the acknowledgements the
// targeted browsers send at that same moment have time to reach the server first.
const POST_DEADLINE_REFRESH_MS = [4000, 12000, 30000];

const remainingWindowMs = (log, now) => remainingClearMs(log.createdAt, now);

const CacheSessionManagement = (props) => {
    const classes = useStyles();
    const { userDetails } = useSelector(state => state.user);
    const isAdmin = !!userDetails?.roles?.find(role => role.name === ROLENAME.Admin);

    // Target selection
    const [targetType, setTargetType] = useState('departments');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [selectedDepartments, setSelectedDepartments] = useState([]);
    const [includeInactive, setIncludeInactive] = useState(false);

    // Options
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [departments, setDepartments] = useState([]);

    // Action
    const [mode, setMode] = useState('soft');
    const [reason, setReason] = useState('');

    // Preview
    const [preview, setPreview] = useState({ count: 0, sample: [] });
    const [previewLoading, setPreviewLoading] = useState(false);

    // Confirmation
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmText, setConfirmText] = useState('');

    // History
    const [history, setHistory] = useState([]);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    // Drives the per-row countdown. Only ticks while at least one row is still inside its window.
    const [now, setNow] = useState(Date.now());

    const previewTimer = useRef(null);
    // Rows seen counting down, so the auto-refresh fires once per row when its window closes and
    // never for rows that were already expired when the page loaded.
    const watchedRows = useRef(new Set());
    // Pending post-deadline re-reads, so they can be cancelled if the admin leaves the page.
    const refreshTimers = useRef([]);

    const buildPayload = useCallback(() => ({
        targetType,
        userIds: selectedUsers.map(user => user._id),
        roleIds: selectedRoles.map(role => role._id),
        departmentIds: selectedDepartments.map(department => department._id),
        includeInactive,
    }), [targetType, selectedUsers, selectedRoles, selectedDepartments, includeInactive]);

    // A selection is only actionable once its own dimension has something picked.
    const hasValidSelection = targetType === 'all'
        || (targetType === 'users' && selectedUsers.length > 0)
        || (targetType === 'roles' && selectedRoles.length > 0)
        || (targetType === 'departments' && selectedDepartments.length > 0);

    const needsTypedConfirmation = targetType === 'all' && mode === 'hard';

    const getOptions = async () => {
        try {
            const response = await HttpService.getCacheClearTargets();
            setUsers(response.data?.users || []);
            setRoles(response.data?.roles || []);
            setDepartments(response.data?.departments || []);
        } catch (error) {
            console.error('Error loading options:', error);
            props.setSnackInfo('Could not load users, roles and departments', 'error');
        }
    };

    const getHistory = async (pageNo = page, limit = rowsPerPage) => {
        try {
            const response = await HttpService.getCacheClearHistory(pageNo + 1, limit);
            setHistory(response.data?.logs || []);
            setHistoryTotal(response.data?.totalCount || 0);
        } catch (error) {
            console.error('Error loading history:', error);
            props.setSnackInfo('Could not load the clear history', 'error');
        }
    };

    useEffect(() => {
        if (!isAdmin) return;
        getOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);

    useEffect(() => {
        if (!isAdmin) return;
        getHistory(page, rowsPerPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin, page, rowsPerPage]);

    // Tick the countdown only while something is actually counting down, so the page is idle the
    // rest of the time.
    useEffect(() => {
        const stillCounting = history.some(log => !log.cancelledAt && remainingWindowMs(log, Date.now()) > 0);
        if (!stillCounting) return undefined;

        const tickId = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(tickId);
    }, [history]);

    useEffect(() => {
        let expired = false;

        history.forEach(log => {
            if (log.cancelledAt) return;
            if (remainingWindowMs(log, now) > 0) {
                watchedRows.current.add(log._id);
                return;
            }
            if (watchedRows.current.has(log._id)) {
                watchedRows.current.delete(log._id);
                expired = true;
            }
        });

        if (expired) {
            POST_DEADLINE_REFRESH_MS.forEach(delay => {
                const timerId = setTimeout(() => getHistory(page, rowsPerPage), delay);
                refreshTimers.current.push(timerId);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [now, history]);

    // Drop any pending re-reads if the admin navigates away mid-countdown.
    useEffect(() => () => {
        refreshTimers.current.forEach(clearTimeout);
        refreshTimers.current = [];
    }, []);

    // Debounced so that picking several departments in a row does not fire a request per click.
    useEffect(() => {
        if (!isAdmin) return undefined;

        if (!hasValidSelection) {
            setPreview({ count: 0, sample: [] });
            return undefined;
        }

        setPreviewLoading(true);
        if (previewTimer.current) clearTimeout(previewTimer.current);

        previewTimer.current = setTimeout(async () => {
            try {
                const response = await HttpService.previewCacheClear(buildPayload());
                setPreview({ count: response.data?.count || 0, sample: response.data?.sample || [] });
            } catch (error) {
                console.error('Error previewing selection:', error);
                setPreview({ count: 0, sample: [] });
            } finally {
                setPreviewLoading(false);
            }
        }, PREVIEW_DEBOUNCE_MS);

        return () => { if (previewTimer.current) clearTimeout(previewTimer.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin, hasValidSelection, buildPayload]);

    const handleTargetTypeChange = (event, value) => {
        if (!value) return;
        setTargetType(value);
        setSelectedUsers([]);
        setSelectedRoles([]);
        setSelectedDepartments([]);
    };

    const openConfirm = () => {
        setConfirmText('');
        setConfirmOpen(true);
    };

    const handleClear = async () => {
        props.setLoading(true);
        try {
            const payload = {
                ...buildPayload(),
                mode,
                reason,
                confirm: needsTypedConfirmation ? confirmText.trim() : undefined,
            };
            const response = await HttpService.triggerCacheClear(payload);
            props.setSnackInfo(
                `Clear sent to ${response.data?.affected || 0} user(s). They get a warning first, then it applies.`,
                'success'
            );
            setConfirmOpen(false);
            setConfirmText('');
            setReason('');
            setPage(0);
            await getHistory(0, rowsPerPage);
        } catch (error) {
            console.error('Error triggering clear:', error);
            props.setSnackInfo(error?.response?.data?.message || 'Could not trigger the clear', 'error');
        }
        props.setLoading(false);
    };

    const handleCancel = async (logId) => {
        props.setLoading(true);
        try {
            const response = await HttpService.cancelCacheClear(logId);
            props.setSnackInfo(
                `Cancelled for ${response.data?.cancelled || 0} user(s) who had not picked it up yet.`,
                'success'
            );
            await getHistory(page, rowsPerPage);
        } catch (error) {
            console.error('Error cancelling clear:', error);
            props.setSnackInfo(error?.response?.data?.message || 'Could not cancel the request', 'error');
        }
        props.setLoading(false);
    };

    const describeTarget = (log) => {
        if (log.targetType === 'all') return 'All Users';
        if (log.targetType === 'roles') return (log.roleIds || []).map(role => role.name).join(', ') || 'Roles';
        if (log.targetType === 'departments') return (log.departmentIds || []).map(d => d.name).join(', ') || 'Departments';
        return (log.userIds || []).map(user => user.name).join(', ') || 'Users';
    };

    if (!isAdmin) return <AccessDenied />;

    return (
        <div className={classes.page}>
            <h2 className={classes.title}>Cache &amp; Session Management</h2>
            <div className={classes.subtitle}>
                Remotely clear cache, cookies, tokens, local storage and session storage in a user&apos;s
                browser. Pick individual users, a role, a department, or everyone.
            </div>

            {/* ---------- Who ---------- */}
            <Paper className={classes.card}>
                <div className={classes.cardHeading}>1. Who should be cleared?</div>

                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={targetType}
                    onChange={handleTargetTypeChange}
                    className={classes.toggleGroup}
                >
                    {TARGET_TYPES.map(option => (
                        <ToggleButton key={option.value} value={option.value} className={classes.toggleBtn}>
                            {option.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>

                {targetType === 'users' && (
                    <Autocomplete
                        multiple
                        size="small"
                        options={users}
                        value={selectedUsers}
                        onChange={(event, value) => setSelectedUsers(value)}
                        getOptionLabel={option => `${option.name}${option.department ? ` - ${option.department}` : ''}${option.isActive === false ? ' (inactive)' : ''}`}
                        getOptionSelected={(option, value) => option._id === value._id}
                        renderInput={params => <TextField {...params} label="Select users" variant="outlined" />}
                    />
                )}

                {targetType === 'roles' && (
                    <Autocomplete
                        multiple
                        size="small"
                        options={roles}
                        value={selectedRoles}
                        onChange={(event, value) => setSelectedRoles(value)}
                        getOptionLabel={option => option.name}
                        getOptionSelected={(option, value) => option._id === value._id}
                        renderInput={params => <TextField {...params} label="Select roles" variant="outlined" />}
                    />
                )}

                {targetType === 'departments' && (
                    <Autocomplete
                        multiple
                        size="small"
                        options={departments}
                        value={selectedDepartments}
                        onChange={(event, value) => setSelectedDepartments(value)}
                        getOptionLabel={option => option.name}
                        getOptionSelected={(option, value) => option._id === value._id}
                        renderInput={params => <TextField {...params} label="Select departments" variant="outlined" />}
                    />
                )}

                {targetType === 'all' && (
                    <Typography variant="body2" style={{ color: '#c0392b' }}>
                        This targets every user in the ERP.
                    </Typography>
                )}

                <FormControlLabel
                    style={{ marginTop: 10 }}
                    control={(
                        <Checkbox
                            checked={includeInactive}
                            onChange={event => setIncludeInactive(event.target.checked)}
                            color="primary"
                        />
                    )}
                    label="Include deactivated users"
                />
            </Paper>

            {/* ---------- What ---------- */}
            <Paper className={classes.card}>
                <div className={classes.cardHeading}>2. What should happen?</div>

                <FormControl component="fieldset">
                    <FormLabel component="legend" style={{ fontSize: 13 }}>Clear type</FormLabel>
                    <RadioGroup value={mode} onChange={event => setMode(event.target.value)}>
                        <FormControlLabel
                            value="soft"
                            control={<Radio color="primary" />}
                            label="Soft Clear - stay signed in"
                            className={classes.modeOption}
                        />
                        <div className={classes.modeHint}>
                            Clears cached data, cookies, session storage and saved preferences. The user
                            stays logged in and their page refreshes with fresh data.
                        </div>
                        <FormControlLabel
                            value="hard"
                            control={<Radio color="primary" />}
                            label="Hard Clear - sign the user out"
                            className={classes.modeOption}
                        />
                        <div className={classes.modeHint}>
                            Clears everything including the login token and the offline database. The user
                            is signed out and sent back to the login screen.
                        </div>
                    </RadioGroup>
                </FormControl>

                <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Reason (optional, saved in the audit log)"
                    value={reason}
                    onChange={event => setReason(event.target.value)}
                    style={{ marginTop: 10 }}
                />
            </Paper>

            {/* ---------- Confirm ---------- */}
            <Paper className={classes.card}>
                <div className={classes.cardHeading}>3. Review and send</div>

                <div className={classes.impactBar}>
                    <PeopleIcon style={{ color: '#1C375D' }} />
                    <span className={classes.impactCount}>
                        {previewLoading ? 'Checking...' : `${preview.count} user(s)`}
                    </span>
                    <span style={{ color: '#777' }}>will be affected</span>
                    {preview.sample.slice(0, 6).map(user => (
                        <Chip key={user._id} size="small" label={user.name} className={classes.sampleChip} />
                    ))}
                    {preview.count > 6 && (
                        <span style={{ color: '#777', fontSize: 13 }}>+{preview.count - 6} more</span>
                    )}
                </div>

                <Button
                    startIcon={<DeleteSweepIcon />}
                    className={mode === 'hard' ? classes.dangerBtn : classes.actionBtn}
                    disabled={!hasValidSelection || previewLoading || preview.count === 0}
                    onClick={openConfirm}
                >
                    {mode === 'hard' ? 'Hard Clear & Sign Out' : 'Soft Clear Cache'}
                </Button>
            </Paper>

            {/* ---------- History ---------- */}
            <Paper className={classes.card} style={{ padding: 0 }}>
                <div className={classes.cardHeading} style={{ padding: '20px 20px 0' }}>Recent clears</div>
                <Typography variant="body2" style={{ color: '#777', padding: '0 20px 12px' }}>
                    &quot;Acknowledged&quot; counts the browsers that have actually picked the clear up.
                </Typography>
                <Divider />
                <TableContainer className={classes.tableContainer}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.headCell}>When</TableCell>
                                <TableCell className={classes.headCell}>By</TableCell>
                                <TableCell className={classes.headCell}>Target</TableCell>
                                <TableCell className={classes.headCell}>Type</TableCell>
                                <TableCell className={classes.headCell}>Acknowledged</TableCell>
                                <TableCell className={classes.headCell}>Reason</TableCell>
                                <TableCell className={classes.headCell}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {history.length === 0 && (
                                <TableRow>
                                    <TableCell className={classes.bodyCell} colSpan={7}>
                                        No clears have been sent yet.
                                    </TableCell>
                                </TableRow>
                            )}
                            {history.map(log => {
                                const pendingUsers = (log.affectedCount || 0) - (log.acknowledgedCount || 0);
                                const remaining = remainingWindowMs(log, now);
                                const counting = !log.cancelledAt && remaining > 0;
                                return (
                                    <TableRow key={log._id} className={classes.rowHover}>
                                        <TableCell className={classes.bodyCell}>
                                            {log.createdAt ? moment(log.createdAt).format('DD MMM YYYY, hh:mm A') : '-'}
                                        </TableCell>
                                        <TableCell className={classes.bodyCell}>
                                            {log.requestedBy?.name || '-'}
                                        </TableCell>
                                        <TableCell className={classes.bodyCell}>
                                            <Tooltip title={describeTarget(log)}>
                                                <span>{describeTarget(log)}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell className={classes.bodyCell}>
                                            <Chip
                                                size="small"
                                                label={log.mode === 'hard' ? 'Hard' : 'Soft'}
                                                className={log.mode === 'hard' ? classes.modeChipHard : classes.modeChipSoft}
                                            />
                                        </TableCell>
                                        <TableCell className={classes.bodyCell}>
                                            {log.acknowledgedCount || 0} / {log.affectedCount || 0}
                                        </TableCell>
                                        <TableCell className={classes.bodyCell}>{log.reason || '-'}</TableCell>
                                        <TableCell className={classes.bodyCell}>
                                            {log.cancelledAt
                                                ? <span style={{ color: '#777' }}>Cancelled</span>
                                                : (
                                                    <>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            disabled={pendingUsers <= 0}
                                                            onClick={() => handleCancel(log._id)}
                                                            style={{ textTransform: 'none' }}
                                                        >
                                                            Cancel
                                                        </Button>
                                                        {counting && (
                                                            <div className={classes.countdown}>
                                                                applies in {formatCountdown(remaining)}
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={historyTotal}
                    page={page}
                    onChangePage={(event, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[25, 50, 100]}
                    onChangeRowsPerPage={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>

            {/* ---------- Confirmation dialog ---------- */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {mode === 'hard' ? 'Hard clear and sign out?' : 'Soft clear cache?'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText component="div">
                        <strong>{preview.count} user(s)</strong>
                        {targetType === 'all' && ' (every user in the ERP)'}
                        {mode === 'hard'
                            ? ' will have their browser storage and login token cleared, and will be signed out.'
                            : ' will have their cached data cleared. They stay signed in.'}
                        <br /><br />
                        This applies in {formatCountdown(CACHE_CLEAR_WINDOW_MS)} minutes. Each user sees a
                        corner warning counting down the same {formatCountdown(CACHE_CLEAR_WINDOW_MS)}, so
                        they can save their work. Until then you can still cancel it from the history
                        table below.
                    </DialogContentText>

                    {needsTypedConfirmation && (
                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            label={`Type "${CONFIRM_PHRASE}" to confirm`}
                            value={confirmText}
                            onChange={event => setConfirmText(event.target.value)}
                            style={{ marginTop: 12 }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} style={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        className={mode === 'hard' ? classes.dangerBtn : classes.actionBtn}
                        disabled={needsTypedConfirmation && confirmText.trim() !== CONFIRM_PHRASE}
                        onClick={handleClear}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default connect(null, { setLoading, setSnackInfo })(CacheSessionManagement);
